import { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";

const LIBRARIES: ("geocoding" | "places")[] = [];
const MAP_STYLE = { width: "100%", height: "280px", borderRadius: "8px" };
const KUZEY_KIBRIS_CENTER = { lat: 35.2, lng: 33.4 };
// Kayıtlı bir konumu tekrar açtığımızda geocode/idle yuvarlamasından kaynaklanan
// birkaç metrelik farkı "konum değişti" saymamak için tolerans payı.
const LOCATION_MATCH_TOLERANCE_METERS = 15;
const METERS_PER_DEGREE_LAT = 111320;

const isSameLocation = (
  a: google.maps.LatLngLiteral,
  b: google.maps.LatLngLiteral
): boolean => {
  const latDiffMeters = (a.lat - b.lat) * METERS_PER_DEGREE_LAT;
  const lngDiffMeters =
    (a.lng - b.lng) * METERS_PER_DEGREE_LAT * Math.cos((a.lat * Math.PI) / 180);
  return Math.hypot(latDiffMeters, lngDiffMeters) <= LOCATION_MATCH_TOLERANCE_METERS;
};

interface Props {
  cityName: string;
  townName: string;
  onPinChange: (lat: number, lng: number) => void;
  initialPos?: { lat: number; lng: number } | null;
  /** Harita "onaylandı" rozetini gösterip göstermediği her değiştiğinde çağrılır —
   *  ebeveyn, konum onayı bozulduğunda kaydet butonunu kilitlemek için kullanır. */
  onConfirmedChange?: (confirmed: boolean) => void;
}

function AddressMap({ cityName, townName, onPinChange, initialPos, onConfirmedChange }: Props) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: LIBRARIES,
    language: "tr",
    region: "TR",
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  // Haritanın o anki merkezi (kullanıcı sürükledikçe güncellenir).
  const [pendingCenter, setPendingCenter] = useState<google.maps.LatLngLiteral | null>(
    initialPos ?? null
  );
  // Onayla'ya son basıldığındaki merkez. pendingCenter ile eşleşiyorsa "onaylandı" rozeti gösterilir.
  const [confirmedCenter, setConfirmedCenter] = useState<google.maps.LatLngLiteral | null>(
    initialPos ?? null
  );
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState("");

  // initialPos geldiğinde (edit modunda parent useEffect'ten) merkezi güncelle
  useEffect(() => {
    setPendingCenter(initialPos ?? null);
    setConfirmedCenter(initialPos ?? null);
    if (initialPos && mapRef.current) {
      mapRef.current.panTo(initialPos);
      mapRef.current.setZoom(16);
    }
  }, [initialPos?.lat, initialPos?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    if (initialPos) {
      map.panTo(initialPos);
      map.setZoom(16);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Şehir + ilçe seçilince geocode et ve o bölgeye zoom yap.
  // Zaten bilinen bir konum varsa (kayıtlı adres düzenleniyorsa) bunu atla —
  // yoksa harita, seçili pinden şehir/ilçe merkezine kayar.
  useEffect(() => {
    if (!isLoaded || !cityName || !townName || initialPos) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { address: `${townName}, ${cityName}, Kıbrıs` },
      (results, status) => {
        if (status === "OK" && results && results[0]) {
          const loc = results[0].geometry.location;
          mapRef.current?.panTo({ lat: loc.lat(), lng: loc.lng() });
          mapRef.current?.setZoom(14);
        }
      }
    );
  }, [isLoaded, cityName, townName]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLocate = () => {
    if (!navigator.geolocation) {
      setLocateError("Tarayıcınız konum özelliğini desteklemiyor.");
      return;
    }
    setLocating(true);
    setLocateError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        mapRef.current?.setCenter(loc);
        setPendingCenter(loc);
        setTimeout(() => mapRef.current?.setZoom(19), 50);
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        if (err.code === err.PERMISSION_DENIED) {
          setLocateError("Konum iznine izin vermediniz. Tarayıcı ayarlarından izin verebilirsiniz.");
        } else {
          setLocateError("Konum alınamadı. Lütfen tekrar deneyin.");
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Harita hareketi bittiğinde (sürükleme/zoom) merkezi güncel tut.
  const handleIdle = useCallback(() => {
    const center = mapRef.current?.getCenter();
    if (!center) return;
    setPendingCenter({ lat: center.lat(), lng: center.lng() });
  }, []);

  const handleConfirm = () => {
    if (!pendingCenter) return;
    setConfirmedCenter(pendingCenter);
    onPinChange(pendingCenter.lat, pendingCenter.lng);
  };

  const isConfirmed =
    !!pendingCenter && !!confirmedCenter && isSameLocation(pendingCenter, confirmedCenter);

  useEffect(() => {
    onConfirmedChange?.(isConfirmed);
  }, [isConfirmed, onConfirmedChange]);

  if (loadError) {
    return (
      <div className="text-center text-muted small py-3">
        <i className="bi bi-exclamation-triangle text-warning me-1"></i>
        Harita yüklenemedi.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="d-flex justify-content-center align-items-center py-4">
        <span className="spinner-border spinner-border-sm text-danger me-2" />
        <span className="small text-muted">Harita yükleniyor...</span>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        className="btn btn-outline-danger btn-sm w-100 mb-2"
        onClick={handleLocate}
        disabled={locating}
      >
        {locating ? (
          <><span className="spinner-border spinner-border-sm me-2" />Konum alınıyor...</>
        ) : (
          <><i className="bi bi-geo-alt-fill me-2"></i>Konumumu Bul</>
        )}
      </button>
      {locateError && (
        <div className="alert alert-warning py-2 small mb-2">
          <i className="bi bi-exclamation-triangle-fill me-1"></i>
          {locateError}
        </div>
      )}

      <div className="address-map-wrapper">
        <GoogleMap
          mapContainerStyle={MAP_STYLE}
          center={KUZEY_KIBRIS_CENTER}
          zoom={10}
          onLoad={onMapLoad}
          onIdle={handleIdle}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            panControl: false,
            zoomControl: true,
            zoomControlOptions: {
              position: window.google.maps.ControlPosition.RIGHT_TOP,
            },
            // Varsayılan "cooperative" mod dokunmatikte kaydırmak için 2 parmak ister;
            // "greedy" tek parmakla/tek elle sürüklemeyi doğrudan haritaya verir.
            gestureHandling: "greedy",
          }}
        />

        {/* Sabit merkez işareti: kullanıcı haritayı sürükleyip evini buraya denk getirir. */}
        <div className="address-map-center-pin">
          {isConfirmed && (
            <div className="address-map-confirmed-badge">
              <i className="bi bi-check-circle-fill me-1"></i>
              Konum seçildi
            </div>
          )}
          <i className="bi bi-plus-lg address-map-crosshair"></i>
        </div>
      </div>

      {!isConfirmed && (
        <button
          type="button"
          className="btn btn-sm w-100 mt-2 text-white"
          style={{ backgroundColor: "#c20e1a" }}
          onClick={handleConfirm}
          disabled={!pendingCenter}
        >
          <i className="bi bi-geo-alt me-2"></i>Haritadaki Konumu Onayla
        </button>
      )}
    </div>
  );
}

export default AddressMap;
